import React from 'react';
import pt from 'prop-types';
import isEqual from 'lodash/isEqual';
import { getAppComponents } from 'utils/app-components';
import { getShownTheme, subscribeShownTheme } from 'utils/shown-theme';
import Label from './label';

function errorMessage(error) {
  return error?.message || 'Component failed to load';
}

function describe(value) {
  if (Array.isArray(value)) return 'a list';
  return typeof value === 'object' ? 'an object' : `a ${typeof value}`;
}

/**
 * A content field rendered by an app's own component in an isolated frame.
 * Implements the admin form contract: onChange(event, value), value(), validate().
 */
export default class InputAppComponent extends React.Component {
  static contextTypes = { client: pt.object };

  static propTypes = {
    appId: pt.string.isRequired,
    component: pt.string.isRequired,
    params: pt.object,
    componentContext: pt.object,
    locale: pt.string,
    valueType: pt.string,
    value: pt.any,
    disabled: pt.bool,
    readonlyContent: pt.bool,
    label: pt.node,
    rawLabel: pt.string,
    labelRenderer: pt.func,
    labelClass: pt.string,
    help: pt.node,
    onChange: pt.func,
    onErrorChange: pt.func,
  };

  // Not named inputRef: Field walks inputRef/refs.input and must stop at this
  // instance to call its validate()
  placeholderRef = React.createRef();

  state = {
    value: this.props.value,
    validity: null,
    typeError: null,
    // Field reads this after a failed validate() to show the message
    error: null,
    loadError: null,
    // An error after the component started; it may recover (a token refresh
    // retries), so it does not block saving and clears when the component reports
    notice: null,
  };

  componentDidMount() {
    this.mountComponent();
  }

  mountComponent() {
    const {
      appId,
      component,
      params,
      componentContext,
      locale,
      label,
      rawLabel,
    } = this.props;
    this.sentValue = this.props.value;
    try {
      this.handle = getAppComponents(this.context.client).mount(
        this.placeholderRef.current,
        {
          app: appId,
          component,
          value: this.props.value,
          context: componentContext || {},
          params: params || {},
          readonly: this.isReadonly(),
          locale,
          theme: getShownTheme(),
          // The frame title and the focus sentinel's name are plain text
          title: (typeof label === 'string' ? label : rawLabel) || component,
        },
      );
    } catch (error) {
      // A broken component must not take the whole form down
      this.handle = null;
      this.setState({ loadError: errorMessage(error) });
      return;
    }
    // A replaced component's late events (its ready rejects on unmount) are ignored
    const { handle } = this;
    const current = () => this.handle === handle;
    // Only errors before the component is ready mean it failed to load
    this.started = false;
    handle.ready?.then(
      () => {
        if (!current()) return;
        this.started = true;
        // It started after all: an earlier error (a token request) is stale
        this.setState({ loadError: null });
      },
      (error) => {
        if (current()) this.setState({ loadError: errorMessage(error) });
      },
    );
    this.unsubscribes = [
      subscribeShownTheme((theme) => {
        if (current()) handle.update({ theme });
      }),
      handle.on('change', (value) => {
        if (current()) this.onComponentChange(value);
      }),
      handle.on('validity', (error) => {
        if (current()) {
          this.setState((state) => ({
            validity: error || null,
            error: state.typeError || error || null,
            notice: null,
          }));
        }
      }),
      handle.on('error', (error) => {
        if (!current()) return;
        if (this.started) {
          this.setState({
            notice: error?.message || 'The component reported an error',
          });
        } else {
          this.setState({ loadError: errorMessage(error) });
        }
      }),
    ];
  }

  componentDidUpdate(prevProps, prevState) {
    // Field shows the message, and updates it once one is shown
    if (this.state.error !== prevState.error) {
      this.props.onErrorChange?.();
    }
    if (
      this.props.appId !== prevProps.appId ||
      this.props.component !== prevProps.component
    ) {
      // Another component: start it from scratch with the current props
      this.unmountComponent();
      this.setState({
        validity: null,
        typeError: null,
        error: null,
        loadError: null,
        notice: null,
      });
      this.mountComponent();
      return;
    }
    if (!this.handle) return;
    const changes = {};
    // The value the component just sent comes back as a prop; do not echo it
    if (
      !isEqual(this.props.value, prevProps.value) &&
      !isEqual(this.props.value, this.sentValue)
    ) {
      changes.value = this.props.value;
      this.sentValue = this.props.value;
      // A replaced value (form reset, external edit) makes an old type error stale
      this.setState({
        value: this.props.value,
        typeError: null,
        error: this.state.validity,
      });
    }
    if (!isEqual(this.props.componentContext, prevProps.componentContext)) {
      changes.context = this.props.componentContext || {};
    }
    if (!isEqual(this.props.params, prevProps.params)) {
      changes.params = this.props.params || {};
    }
    if (this.isReadonly() !== this.isReadonly(prevProps)) {
      changes.readonly = this.isReadonly();
    }
    if (this.props.locale !== prevProps.locale) {
      changes.locale = this.props.locale;
    }
    if (Object.keys(changes).length > 0) {
      this.handle.update(changes);
    }
  }

  componentWillUnmount() {
    this.unmountComponent();
  }

  unmountComponent() {
    this.unsubscribes?.forEach((unsubscribe) => unsubscribe?.());
    this.unsubscribes = null;
    const { handle } = this;
    this.handle = null;
    handle?.unmount();
  }

  isReadonly(props = this.props) {
    return Boolean(props.disabled || props.readonlyContent);
  }

  // The frame shows a value as soon as the component sets it; a value the
  // field does not take is replaced with the field's own
  restoreValue() {
    this.handle?.update({ value: this.state.value });
  }

  onComponentChange = (sent) => {
    // The component is told it is read-only; a change sent anyway is dropped
    if (this.isReadonly()) {
      this.restoreValue();
      return;
    }
    // An undefined value is empty: Field reads the event target otherwise
    const value = sent === undefined ? null : sent;
    // Content-only component fields are stored as text; never coerce. Null
    // clears the field like any other input
    if (
      this.props.valueType === 'string' &&
      value !== null &&
      typeof value !== 'string'
    ) {
      const typeError = `The component sent ${describe(
        value,
      )}; this field stores text.`;
      this.setState({ typeError, error: typeError });
      this.restoreValue();
      return;
    }
    const changed = !isEqual(value, this.state.value);
    this.sentValue = value;
    this.setState({
      value,
      typeError: null,
      error: this.state.validity,
      notice: null,
    });
    if (changed) {
      this.props.onChange?.(new Event('change', { bubbles: true }), value);
    }
  };

  value() {
    return this.state.value;
  }

  validate() {
    return !this.state.validity && !this.state.typeError;
  }

  // Form only counts a field's error when its input can focus and is laid out.
  // The SDK moves focus to the component's first control.
  focus() {
    this.handle?.focus();
  }

  // Form treats this instance as its input element: it reads the layout and
  // scrolls to it with jQuery (offset, closest), so these come from the placeholder
  get offsetParent() {
    return this.placeholderRef.current?.offsetParent ?? null;
  }

  get parentNode() {
    return this.placeholderRef.current?.parentNode ?? null;
  }

  get ownerDocument() {
    return this.placeholderRef.current?.ownerDocument ?? document;
  }

  getClientRects() {
    return this.placeholderRef.current?.getClientRects() ?? [];
  }

  getBoundingClientRect() {
    return (
      this.placeholderRef.current?.getBoundingClientRect() ?? new DOMRect()
    );
  }

  render() {
    const { label, labelRenderer, labelClass, help } = this.props;
    // Type errors and validity messages are shown by Field after validate()
    const { loadError, notice } = this.state;
    // The SDK adds children to the placeholder; React never renders into it
    return (
      <div className="form-app-component">
        <Label
          label={label}
          labelRenderer={labelRenderer}
          help={help}
          className={labelClass}
        />
        <div ref={this.placeholderRef} className="form-app-component-frame" />
        {loadError ? <div className="form-field-error">{loadError}</div> : null}
        {!loadError && notice ? (
          <div className="form-field-warning">{notice}</div>
        ) : null}
      </div>
    );
  }
}
